import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Newspaper,
  PenLine,
  Plus,
  User,
} from 'lucide-react';
import {
  createAdminBlog,
  fetchAdminBlog,
  fetchAdminBlogs,
  patchAdminBlog,
  type AdminBlogRow,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

function CreateBlogModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: '',
    author: '',
    excerpt: '',
    status: 'draft',
    publishedAt: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({ title: '', author: '', excerpt: '', status: 'draft', publishedAt: '' });
    setError('');
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      createAdminBlog({
        title: form.title.trim(),
        author: form.author.trim(),
        excerpt: form.excerpt.trim() || undefined,
        status: form.status,
        publishedAt: form.publishedAt || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-blogs'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create blog post');
    },
  });

  if (!open) return null;

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-admin-modal glass-surface"
        role="dialog"
        aria-labelledby="create-blog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-admin-modal-head">
          <div>
            <p className="wfx-admin-breadcrumb">New post</p>
            <h2 id="create-blog-title"><PenLine size={20} /> Create blog post</h2>
          </div>
        </header>
        <form
          className="wfx-admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            create.mutate();
          }}
        >
          <label className="wfx-admin-field">
            <span>Title</span>
            <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </label>
          <label className="wfx-admin-field">
            <span>Author</span>
            <input required value={form.author} onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))} />
          </label>
          <label className="wfx-admin-field">
            <span>Excerpt</span>
            <textarea
              rows={3}
              value={form.excerpt}
              onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
              className="wfx-admin-textarea"
            />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </label>
            <label className="wfx-admin-field">
              <span>Publish date</span>
              <input
                type="date"
                value={form.publishedAt}
                onChange={(e) => setForm((f) => ({ ...f, publishedAt: e.target.value }))}
              />
            </label>
          </div>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BlogDetailCard({
  blogId,
  onClose,
}: {
  blogId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-blog', blogId],
    queryFn: () => fetchAdminBlog(blogId!),
    enabled: Boolean(blogId),
  });

  const update = useMutation({
    mutationFn: (body: Partial<AdminBlogRow>) => patchAdminBlog(blogId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-blogs'] });
      void qc.invalidateQueries({ queryKey: ['admin-blog', blogId] });
    },
  });

  const post = data?.post;

  return (
    <AdminDetailCard
      open={Boolean(blogId)}
      onClose={onClose}
      ariaLabel="Blog post details"
      breadcrumb="Blog post"
      title={post?.title ?? 'Loading…'}
      illustration="admin.module.cms"
      subtitle={
        post ? (
          <>
            <AdminStatusBadge status={post.status} />
            <span className="wfx-muted wfx-admin-subline">/{post.slug}</span>
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading blog post…" />}
      {isError && <ErrorState message="Could not load blog post." onRetry={() => void refetch()} />}

      {post && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Author</dt><dd>{post.author}</dd></div>
            <div><dt>Status</dt><dd><AdminStatusBadge status={post.status} /></dd></div>
            <div><dt>Published</dt><dd>{post.publishedAt ?? '—'}</dd></div>
            <div><dt>Updated</dt><dd>{post.updatedAt ? new Date(post.updatedAt).toLocaleDateString() : '—'}</dd></div>
          </dl>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Edit post</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Title</span>
                  <input
                    defaultValue={post.title}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== post.title) update.mutate({ title: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Author</span>
                  <input
                    defaultValue={post.author}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== post.author) update.mutate({ author: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Excerpt</span>
                  <textarea
                    rows={3}
                    className="wfx-admin-textarea"
                    defaultValue={post.excerpt ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (post.excerpt ?? '')) update.mutate({ excerpt: value || null });
                    }}
                  />
                </label>
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>Status</span>
                    <select
                      className="wfx-admin-select"
                      value={post.status}
                      disabled={update.isPending}
                      onChange={(e) => update.mutate({ status: e.target.value })}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="scheduled">Scheduled</option>
                    </select>
                  </label>
                  <label className="wfx-admin-field">
                    <span>Publish date</span>
                    <input
                      type="date"
                      defaultValue={post.publishedAt ?? ''}
                      onBlur={(e) => {
                        const value = e.target.value || null;
                        if (value !== post.publishedAt) update.mutate({ publishedAt: value });
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <h3>Content & SEO</h3>
              <label className="wfx-admin-field">
                <span>Body</span>
                <textarea
                  rows={5}
                  className="wfx-admin-textarea"
                  defaultValue={post.content ?? ''}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value !== (post.content ?? '')) update.mutate({ content: value || null });
                  }}
                />
              </label>
              <label className="wfx-admin-field">
                <span>SEO title</span>
                <input
                  defaultValue={post.seoTitle ?? ''}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value !== (post.seoTitle ?? '')) update.mutate({ seoTitle: value || null });
                  }}
                />
              </label>
              <label className="wfx-admin-field">
                <span>SEO description</span>
                <textarea
                  rows={2}
                  className="wfx-admin-textarea"
                  defaultValue={post.seoDescription ?? ''}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value !== (post.seoDescription ?? '')) update.mutate({ seoDescription: value || null });
                  }}
                />
              </label>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function BlogCard({
  post,
  onSelect,
}: {
  post: AdminBlogRow;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="wfx-admin-blog-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-blog-card-head">
        <AdminStatusBadge status={post.status} />
        <span className="wfx-muted">{post.publishedAt ?? 'Unscheduled'}</span>
      </div>
      <strong>{post.title}</strong>
      <p className="wfx-muted wfx-admin-blog-card-excerpt">{post.excerpt ?? 'No excerpt yet.'}</p>
      <span className="wfx-admin-blog-card-author"><User size={14} aria-hidden="true" /> {post.author}</span>
    </button>
  );
}

export function AdminBlogsScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedBlogId, setSelectedBlogId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, statusFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-blogs', debouncedQuery, statusFilter, page],
    queryFn: () =>
      fetchAdminBlogs({
        q: debouncedQuery || undefined,
        status: statusFilter,
        page,
        limit: 12,
      }),
  });

  const posts = data?.data ?? [];
  const stats = data?.stats;
  const pagination = data?.pagination;

  const kpiItems = useMemo(
    () => [
      { label: 'Total posts', value: stats?.total ?? 0 },
      { label: 'Published', value: stats?.published ?? 0 },
      { label: 'Drafts', value: stats?.draft ?? 0 },
      { label: 'Scheduled', value: stats?.scheduled ?? 0 },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading blogs…" />;
  if (isError) return <ErrorState message="Could not load blogs." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Content / Blogs"
        title="Blogs"
        description="Publish and schedule blog posts for exam preparation guides and platform updates."
        icon={Newspaper}
        illustration="admin.module.cms"
        actions={(
          <button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}>
            <Plus size={16} aria-hidden="true" />
            New post
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-blog-grid">
          {posts.map((post) => (
            <BlogCard key={post.id} post={post} onSelect={() => setSelectedBlogId(post.id)} />
          ))}
        </div>

        <AdminTablePanel
          title="All posts"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput value={query} onChange={setQuery} placeholder="Search title, author, slug…" />
              <select
                className="wfx-admin-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="all">All statuses</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>
          )}
          footer={
            pagination && pagination.pages > 1 ? (
              <div className="wfx-admin-pagination">
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                  Previous
                </button>
                <span className="wfx-muted">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} posts
                </span>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined
          }
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Title</th><th>Author</th><th>Status</th><th>Published</th><th>Slug</th></tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr
                  key={post.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedBlogId(post.id)}
                >
                  <td><strong>{post.title}</strong></td>
                  <td>{post.author}</td>
                  <td><AdminStatusBadge status={post.status} /></td>
                  <td>{post.publishedAt ?? '—'}</td>
                  <td><code>{post.slug}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
          {posts.length === 0 && <AdminEmptyState message="No blog posts match your filters." />}
        </AdminTablePanel>
      </AdminPageLayout>

      <CreateBlogModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <BlogDetailCard blogId={selectedBlogId} onClose={() => setSelectedBlogId(null)} />
    </>
  );
}
